/**
 * Equity Calculation Utilities
 * Pure helper functions extracted from index.js for reuse in
 * custom reports, cap table columns, and the dashboard.
 *
 * All functions are pure (no side effects, no global state).
 */

/**
 * Check if a transaction involves any of the given user IDs
 * @param {Object} transaction - Transaction object with sender/receiver
 * @param {string|string[]} userIdOrArray - Single userid or array of userids
 * @returns {boolean}
 */
export function isUserTransaction(transaction, userIdOrArray) {
	if (!transaction) return false;
	const userIds = Array.isArray(userIdOrArray)
		? userIdOrArray.map((id) => id?.toLowerCase()).filter(Boolean)
		: [userIdOrArray?.toLowerCase()].filter(Boolean);

	if (!userIds.length) return false;

	const sender = transaction.sender?.toLowerCase();
	const receiver = transaction.receiver?.toLowerCase();

	return userIds.some((uid) => sender === uid || receiver === uid);
}

/**
 * Get the array of active user IDs (supports consolidated view)
 * @param {Object} userData - User data object
 * @returns {string[]} Array of user IDs
 */
export function getActiveUserIds(userData) {
	if (!userData) return [];
	return userData.consolidatedUserIds || [userData.userid];
}

/**
 * Check if the current view is a consolidated (multi-account) view
 * @param {Object} userData - User data object
 * @returns {boolean}
 */
export function isConsolidatedView(userData) {
	return userData?.userid === 'consolidated.view';
}

/**
 * Get the signed quantity of a transaction for a user.
 * Positive = user is receiver, negative = user is sender.
 * @param {Object} t - Transaction object
 * @param {string|string[]} userIdOrArray - Single userid or array of userids
 * @returns {number} Signed quantity
 */
export function getSignedQuantityForUser(t, userIdOrArray) {
	if (!t || !userIdOrArray) return 0;

	let q = Number(t.quantity) || 0;
	if (!q) return 0;

	// If quantity already encodes direction, trust it
	if (q < 0) return q;

	// Support array of userIds for consolidated view
	const userIds = Array.isArray(userIdOrArray)
		? userIdOrArray.map((id) => id?.toLowerCase()).filter(Boolean)
		: [userIdOrArray?.toLowerCase()].filter(Boolean);

	if (!userIds.length) return 0;

	const sender = t.sender?.toLowerCase();
	const receiver = t.receiver?.toLowerCase();

	// Quantity is positive: infer direction
	if (sender && userIds.includes(sender)) return -q;
	if (receiver && userIds.includes(receiver)) return q;

	// Not related to this user
	return 0;
}

/**
 * Get all transactions for a share class that involve the given user(s)
 * @param {Object} equityNode - clientData.equity[shareClass] object
 * @param {string|string[]} userIdOrArray - Single userid or array of userids
 * @returns {Object[]} Filtered transactions
 */
export function getUserTransactionsForClass(equityNode, userIdOrArray) {
	if (!equityNode?.transactions || !userIdOrArray) return [];

	// Support array of userIds for consolidated view
	const userIds = Array.isArray(userIdOrArray)
		? userIdOrArray.map((id) => id?.toLowerCase()).filter(Boolean)
		: [userIdOrArray?.toLowerCase()].filter(Boolean);

	if (!userIds.length) return [];

	return equityNode.transactions.filter((t) => {
		if (!t) return false;
		const sender = t.sender?.toLowerCase();
		const receiver = t.receiver?.toLowerCase();
		return userIds.some((uid) => sender === uid || receiver === uid);
	});
}

/**
 * Check if a transaction represents an active (non-expired) tranche
 * @param {Object} t - Transaction with strikePrice and optional expDate
 * @param {number} nowSec - Current time in seconds since epoch
 * @returns {boolean}
 */
export function isActiveTranche(t, nowSec) {
	if (!t?.strikePrice) return false;
	if (!t.expDate) return true;
	return Number(t.expDate) > nowSec;
}

/**
 * Calculate the effective (weighted average) strike price for a user in a share class.
 * Nets contra transactions per tranche and weights by outstanding quantity.
 * @param {Object} equityNode - clientData.equity[shareClass] object
 * @param {string|string[]} userId - Single userid or array of userids
 * @param {Object} [opts={}] - Options
 * @param {boolean} [opts.useWeighted=true] - Use weighted average across tranches
 * @param {boolean} [opts.netContra=true] - Net negatives against positives per tranche
 * @param {boolean} [opts.outstandingOnly=true] - Only count tranches with netQty > 0
 * @param {number} [opts.nowSec] - Override current time (seconds since epoch)
 * @returns {number} Effective strike price
 */
export function getEffectiveStrikePriceForUser(equityNode, userId, opts = {}) {
	const nowSec = opts.nowSec ?? Date.now() / 1000;
	const useWeighted = opts.useWeighted !== false;
	const netContra = opts.netContra !== false;
	const outstandingOnly = opts.outstandingOnly !== false;

	const tx = getUserTransactionsForClass(equityNode, userId);
	if (!tx.length) return 0;

	// simple fallback: last strikePrice found
	let lastStrike = 0;
	tx.forEach((t) => {
		if (t?.strikePrice) lastStrike = Number(t.strikePrice) || lastStrike;
	});

	if (!useWeighted) return lastStrike;

	const tranches = tx.filter((t) => isActiveTranche(t, nowSec));
	if (tranches.length <= 1) {
		return tranches.length === 1 ? Number(tranches[0].strikePrice) || 0 : lastStrike;
	}

	// Weighted avg
	let agg = 0;
	let ttl = 0;

	if (netContra) {
		// Net by tranche key (strike|exp); upgrade key if you have grant/award id.
		let trancheMap = {};
		tranches.forEach((t) => {
			const strike = Number(t.strikePrice) || 0;
			const exp = t.expDate ? String(t.expDate) : '';
			const k = strike + '|' + exp;
			const q = getSignedQuantityForUser(t, userId);
			trancheMap[k] = (trancheMap[k] || 0) + q;
		});

		Object.keys(trancheMap).forEach((k) => {
			const netQty = trancheMap[k];
			const strike = Number(k.split('|')[0]) || 0;
			if (outstandingOnly && !(netQty > 0)) return;
			agg += netQty * strike;
			ttl += netQty;
		});
	} else {
		// Old behavior: only positive quantities count
		tranches.forEach((t) => {
			const q = Number(t.quantity) || 0;
			const strike = Number(t.strikePrice) || 0;
			if (outstandingOnly && !(q > 0)) return;
			agg += q * strike;
			ttl += q;
		});
	}

	if (!ttl) return 0;
	return agg / ttl;
}

/**
 * Calculate vested shares from a vesting schedule.
 * Parses the vesting object to determine vested status via:
 *   1. Tranche-level 'vested' flag (BW-style)
 *   2. Entry-level 'vested' flag
 *   3. Date-based check (timestamp in past = vested)
 *
 * @param {Object} vestingObj - Vesting schedule object (e.g., { "Grant 1": { schedule: { timestamp: { amount } } } })
 * @param {number} owned - Total owned shares
 * @returns {number} Number of currently vested shares
 */
export function calculateVestedFromSchedule(vestingObj, owned) {
	if (!vestingObj || typeof vestingObj !== 'object') {
		return owned; // No vesting schedule = all owned shares are vested
	}

	const now = Math.floor(Date.now() / 1000); // Current Unix timestamp
	let totalVested = 0;
	let totalShares = 0;

	// Loop through all vesting entries (e.g., "Payroll Withholding", "Grant 1", etc.)
	let hasVestingEntries = false;
	Object.values(vestingObj).forEach((vestingEntry) => {
		if (!vestingEntry || typeof vestingEntry !== 'object') return;
		hasVestingEntries = true;
		if (vestingEntry.cashedOut === true) return; // skip cashed-out tranches

		// Handle schedule-based vesting
		if (vestingEntry.schedule && typeof vestingEntry.schedule === 'object') {
			// Check for tranche-level vested flag (BW-style: vested flag on parent entry, not schedule items)
			// Back-compat: if no "vested" flag, check for threshold presence (means vested)
			const trancheHasVestedFlag = 'vested' in vestingEntry;
			const isTrancheVested = trancheHasVestedFlag ? !!vestingEntry.vested : 'threshold' in vestingEntry;

			Object.entries(vestingEntry.schedule).forEach(([timestamp, entry]) => {
				const ts = Number(timestamp);
				const hasTs = Number.isFinite(ts);
				const amount = Number(entry?.amount || 0);
				const isForfeited = !!entry?.forfeited;

				if (amount === 0) return;
				totalShares += amount;

				// Determine vested status:
				// 1. If tranche has explicit 'vested' flag, use that for all entries in this tranche
				// 2. If entry itself has 'vested' flag, use that
				// 3. Otherwise fall back to date-based check (timestamp in past = vested)
				// Forfeited entries don't count as vested
				let isVested = false;
				if (!isForfeited) {
					if (trancheHasVestedFlag) {
						isVested = isTrancheVested;
					} else if ('vested' in entry) {
						isVested = !!entry.vested;
					} else {
						isVested = hasTs && ts <= now;
					}
				}
				if (isVested) {
					totalVested += amount;
				}
			});
		}

		// Handle goal-based vesting (milestones)
		if (vestingEntry.goals && typeof vestingEntry.goals === 'object') {
			Object.values(vestingEntry.goals).forEach((goal) => {
				const amount = Number(goal?.amount || 0);
				if (amount === 0) return;
				totalShares += amount;
				if (goal?.vested) {
					totalVested += amount;
				}
			});
		}
	});

	// If we found vesting data, return calculated vested amount
	// Otherwise fall back to owned (no vesting = all vested)
	return totalShares > 0 ? totalVested : hasVestingEntries ? 0 : owned;
}

/**
 * Render-safe basis for a calculator's breakdown[] entry.
 *
 * Returns null for the -1 "no data" sentinel (and missing / non-numeric / NaN),
 * so templates with {{#if basis}} guards cleanly suppress the line. **Real
 * negative values pass through unchanged** — a shareholder whose sales netted
 * below their purchases has a legitimately negative basis, and the dashboard
 * has rendered those as e.g. "-$760" for a long time. This helper preserves
 * that behavior so the admin preview and dashboard stay consistent.
 *
 * @param {Object} holdings - Calculator input holdings (has .bases map)
 * @param {string} key - Share class key
 * @returns {number|null}
 */
export function basisForBreakdown(holdings, key) {
	const v = holdings?.bases?.[key];
	if (typeof v !== 'number' || Number.isNaN(v)) return null;
	if (v === -1) return null; // documented sentinel for "no data"
	return v;
}
